import axios from 'axios';
import { auth } from '../../firebase';
import config from '../config';

// Create axios instance with proper base URL
const apiAxios = axios.create({
  baseURL: config.API_BASE_URL,
  withCredentials: true  // Important for cookies
});

class TokenManager {
  constructor() {
    this.cachedToken = null;
    this.tokenExpirationTime = null;
    this.refreshPromise = null;
  }

  async getToken() {
    const currentTime = Date.now();

    // Return cached token if valid
    if (this.cachedToken && this.tokenExpirationTime && currentTime < this.tokenExpirationTime) {
      return this.cachedToken;
    }

    // Prevent concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Refresh token
    this.refreshPromise = this._refreshToken();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async _refreshToken() {
    const user = auth.currentUser;
    if (!user) {
      this.cachedToken = null;
      this.tokenExpirationTime = null;
      return null;
    }

    try {
      this.cachedToken = await user.getIdToken(true);
      // Set expiration to 55 minutes (5 minute buffer before actual expiry)
      this.tokenExpirationTime = Date.now() + 55 * 60 * 1000;

      // NEW: Also refresh session cookie whenever ID token refreshes
      await this.refreshSessionCookie(this.cachedToken);

      return this.cachedToken;
    } catch (error) {
      console.error('[TokenManager] Failed to refresh token:', error);
      this.cachedToken = null;
      this.tokenExpirationTime = null;
      throw error;
    }
  }

  // NEW METHOD: Refresh session cookie
  async refreshSessionCookie(idToken) {
    try {
      await apiAxios.post('/cellcog/session/login', {}, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
    } catch (error) {
      console.error('[TokenManager] Failed to refresh session cookie:', error);
      // Don't throw - session cookie failure shouldn't break ID token flow
    }
  }

  clearCache() {
    this.cachedToken = null;
    this.tokenExpirationTime = null;
  }

  isTokenValid() {
    return this.cachedToken && this.tokenExpirationTime && Date.now() < this.tokenExpirationTime;
  }

  // Get time until token expiry in milliseconds
  getTimeUntilExpiry() {
    if (!this.tokenExpirationTime) return 0;
    const remaining = this.tokenExpirationTime - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  // Force a token refresh (useful for testing or manual refresh)
  async forceRefresh() {
    this.cachedToken = null;
    this.tokenExpirationTime = null;
    this.refreshPromise = null;
    return this.getToken();
  }
}

// Export singleton instance
const tokenManager = new TokenManager();
export default tokenManager;
